import React, { useReducer, useEffect, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Input } from '../components/Common';
import { DndContext, DragOverlay, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import SectionPalette from '../components/templateEditor/SectionPalette';
import SectionList from '../components/templateEditor/SectionList';
import PageSettingsPanel from '../components/templateEditor/PageSettingsPanel';
import HeaderFooterEditor from '../components/templateEditor/HeaderFooterEditor';
import TemplatePreview from '../components/templateEditor/TemplatePreview';
import VariableReference from '../components/templateEditor/VariableReference';

import { tsrDashboardConfig } from '../components/templateEditor/tsrDashboard';
import { tsrFindingsWalkthroughConfig } from '../components/templateEditor/tsrFindingsWalkthrough';
import { nextStepsDefaultConfig } from '../components/templateEditor/nextStepsDefaults';
function generateId(prefix = 'sec') {
  return prefix + '_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
}

/**
 * A saved section, ready to go on the canvas.
 *
 * Deep-copied and re-identified, both of which matter. Two inserts of the same
 * saved section would otherwise share one config object, so editing the text
 * in either would change both; and they would share one section id, which is
 * the key React reorders by AND the key the PDF renderer maps page numbers
 * against, so the contents page would point at the wrong one.
 */
function sectionFromSaved(saved, label) {
  const stored = saved.section_json || {};
  const copy = JSON.parse(JSON.stringify(stored.config || {}));
  if (Array.isArray(copy.components)) {
    copy.components = copy.components.map(c => ({ ...c, id: generateId('comp') }));
  }
  return {
    id: generateId(),
    type: stored.type,
    // The saved name is what the author chose for it, so it is what the
    // section is called on the canvas. Its original label is the fallback.
    label: label || saved.name || stored.label || 'Saved Section',
    config: copy,
  };
}

const DEFAULT_CONFIGS = {
  'title-page': { showLogo: true, showDate: true, showTester: true, showConfidential: true, titleText: 'Penetration Testing Report' },
  'table-of-contents': { heading: 'Table of Contents', pageBreakBefore: true },
  'rich-text': { heading: '', html: '', pageBreakBefore: false },
  'severity-dashboard': { heading: 'Finding Dashboard', showChart: true, showTable: true },
  'risk-rating-table': { heading: 'Risk Rating Methodology' },
  'scope-table': { heading: 'Scope', pageBreakBefore: true },
  'findings-summary': { heading: 'Vulnerability Summary Table', columns: ['#', 'title', 'severity', 'cvss', 'status'], pageBreakBefore: true },
  'findings-detail': { heading: 'Detailed Findings', showEvidence: true, onePerPage: true, showFields: ['severity', 'cvss', 'details', 'affectedEndpoints', 'observation', 'stepsToReproduce', 'evidence', 'recommendation', 'references'], pageBreakBefore: true },
  'page-break': {},
  'horizontal-line': { color: '#cccccc', thickness: 1, width: 100 },
  'spacer': { height: 20 },
  'version-history': { heading: 'Document History', allTemplates: false },
  'metric-tiles': {
    heading: '', perRow: 5, labelSize: 8, valueSize: 16, upperCaseLabels: true,
    // The literal all three renderers fall back to, written out so a band
    // that has never been touched and one saved untouched print alike.
    tileAlign: 'center',
    // Seeded with the severity counts, because that is the row people reach for
    // first and it shows at a glance that values take report variables.
    tiles: [
      { label: 'Critical', value: '%CRITICALCOUNT%', bg: '#7f1d1d', labelColor: '#ffffff', valueColor: '#ffffff' },
      { label: 'High', value: '%HIGHCOUNT%', bg: '#dc2626', labelColor: '#ffffff', valueColor: '#ffffff' },
      { label: 'Medium', value: '%MEDIUMCOUNT%', bg: '#ea580c', labelColor: '#ffffff', valueColor: '#ffffff' },
      { label: 'Low', value: '%LOWCOUNT%', bg: '#ca8a04', labelColor: '#ffffff', valueColor: '#ffffff' },
      { label: 'Informational', value: '%INFOCOUNT%', bg: '#2e86c1', labelColor: '#ffffff', valueColor: '#ffffff' },
    ],
  },
  // Defaults are the house styling, so the band looks finished the moment it
  // is dropped in. Every one of them is reachable from its settings panel --
  // the point of a widget is that somebody can make it theirs without editing
  // a renderer. These literals are duplicated in the DOCX and PDF renderers as
  // their own fallbacks, so a config that has never been touched and one that
  // was saved untouched produce the same output.
  'findings-walkthrough': {
    heading: '', columns: ['finding', 'severity', 'cvss', 'status'],
    headerBg: '#1f3864', headerColor: '#ffffff', headerSize: 9, headerBold: true,
    titleColor: '#1f3864', titleSize: 9, showUid: true, uidSeparator: '  |  ',
    showDescription: true, descriptionField: 'details',
    descriptionColor: '#5f6b7a', descriptionSize: 8,
    severityStyle: 'block', severityTextColor: '#ffffff', severitySize: 8, severityUpper: true,
    cvssColor: '#1f3864', cvssSize: 9, statusColor: '#c00000', statusSize: 9, statusBold: true,
    borderColor: '#d8dfe8', severityFilter: [], maxRows: 0,
    // Written explicitly, because the renderers treat an absent nextSteps key
    // as "this widget has no Next steps block" -- which is what stops a
    // template saved before the block existed from printing it twice.
    nextSteps: nextStepsDefaultConfig(),
  },
  // The same literals renderRevalidationSummary falls back to, so a band that
  // has never been touched and one saved untouched print alike. The default
  // columns are the full before/after shape; unticking the two status columns
  // turns the same band into a plain summary carrying the original risk.
  'revalidation-summary': {
    heading: '', columns: ['num', 'title', 'originalRisk', 'originalStatus', 'newStatus'],
    headerBg: '#24416d', headerColor: '#ffffff', headerSize: 9, headerBold: true,
    titleSize: 9, titleBold: false, valueSize: 8,
    severityStyle: 'text', severityUpper: true,
    statusStyle: 'text', statusUpper: true, valueTextColor: '#ffffff',
    borderColor: '#d8dfe8', severityFilter: [], maxRows: 0,
    includeOriginallyClosed: false,
    emptyText: 'No findings were revalidated.', emptyTextColor: '#5f6b7a',
  },
  // The same literals the DOCX and PDF renderers fall back to, so a band
  // that has never been touched and one saved untouched print alike.
  'owasp-findings': {
    heading: 'Findings by OWASP Top 10 (2021) Category',
    columns: ['category', 'findings', 'count'],
    headerBg: '#1f3864', headerColor: '#ffffff', headerSize: 9, headerBold: true,
    borderColor: '#d8dfe8', bodySize: 9,
    categoryColor: '#1f3864', findingsColor: '#1f3864', countColor: '#1f3864',
    categoryWidth: 3000, countWidth: 900, countAlign: 'left',
    // On, so the Count column totals the engagement's findings. Off would
    // drop the unclassified ones out of a table that looks complete.
    showUnclassified: true, showAllCategories: false,
    findingField: 'uid', severityFilter: [],
  },
  // The same literals both renderers fall back to. metaRows and
  // bodySections are deliberately NOT written here: an absent key means
  // 'the house layout', and writing it out would freeze a copy of that
  // layout into every template made from today, so improving it later
  // would reach none of them.
  'finding-cards': {
    heading: 'Technical Findings',
    titleBg: '#24416d', titleColor: '#ffffff', titleSize: 11, titleBold: true,
    showUid: true, titleSeparator: '  |  ', badgeWidth: 1800, severityUpper: true,
    severityTextColor: '#ffffff',
    labelBg: '#f2f5f9', labelColor: '#24416d', labelSize: 10, labelBold: true,
    valueColor: '#000000', valueSize: 10, statusColor: '#c0392b', labelWidth: 1700,
    hideEmpty: true, showBorders: false, borderColor: '#d8dfe8',
    sectionLabelColor: '#1f3864', sectionLabelSize: 11, sectionLabelBold: true,
    bodyColor: '#000000', bodySize: 10,
    sectionsInContents: false, showEvidence: true, figurePrefix: 'Figure',
    figureCaptionColor: '#5f6b7a', figureCaptionSize: 9,
    severityFilter: [], onePerPage: false, cardGap: 200, maxCards: 0,
  },
  'heading': { text: 'Section Heading', level: 2 },
  'text-block': { html: '' },
  'custom-table': { heading: '', columns: [{ label: 'Column 1' }, { label: 'Column 2' }], rows: [['', '']] },
  'image': { heading: '', imageData: null, caption: '', width: 80 },
  'severity-chart': { heading: 'Severity Distribution', chartType: 'pie' },
  'composite': { heading: 'Custom Section', components: [] },
};

function templateReducer(state, action) {
  switch (action.type) {
    case 'SET_TEMPLATE':
      return action.payload;
    case 'ADD_SECTION': {
      // The house pages are presets, not section types: each lands as an
      // ordinary Custom Section so the renderers need to know nothing about
      // it and every band it contains stays editable and removable.
      const PRESETS = {
        'tsr-dashboard': tsrDashboardConfig,
        'tsr-findings-walkthrough': tsrFindingsWalkthroughConfig,
      };
      const preset = PRESETS[action.payload.type];
      const cfg = preset ? preset() : { ...DEFAULT_CONFIGS[action.payload.type] };
      if (preset) {
        // Components carry ids of their own, and two dashboards in one
        // template must not share them.
        cfg.components = (cfg.components || []).map(c => ({ ...c, id: generateId() }));
      }
      const newSection = {
        id: generateId(),
        type: preset ? 'composite' : action.payload.type,
        label: action.payload.label,
        config: cfg
      };
      const sections = [...(state.sections || [])];
      if (action.payload.index != null) {
        sections.splice(action.payload.index, 0, newSection);
      } else {
        sections.push(newSection);
      }
      return { ...state, sections };
    }
    case 'ADD_SAVED_SECTION': {
      const newSection = sectionFromSaved(action.payload.saved, action.payload.label);
      if (!newSection.type) return state;
      const sections = [...(state.sections || [])];
      if (action.payload.index != null) {
        sections.splice(action.payload.index, 0, newSection);
      } else {
        sections.push(newSection);
      }
      return { ...state, sections };
    }
    case 'REMOVE_SECTION':
      return { ...state, sections: (state.sections || []).filter(s => s.id !== action.payload) };
    case 'REORDER_SECTIONS':
      return { ...state, sections: action.payload };
    case 'UPDATE_SECTION':
      return {
        ...state,
        sections: (state.sections || []).map(s =>
          s.id === action.payload.id ? { ...s, ...action.payload.updates } : s
        )
      };
    case 'MOVE_TO_TITLE_PAGE': {
      const { sectionId } = action.payload;
      const allSections = state.sections || [];
      const titlePage = allSections.find(s => s.type === 'title-page');
      if (!titlePage) return state;
      const sourceSection = allSections.find(s => s.id === sectionId);
      if (!sourceSection) return state;
      const comp = { id: 'comp_' + sectionId, type: sourceSection.type, label: sourceSection.label, config: { ...sourceSection.config } };
      return {
        ...state,
        sections: allSections.filter(s => s.id !== sectionId).map(s =>
          s.id === titlePage.id ? { ...s, config: { ...s.config, components: [...(s.config?.components || []), comp] } } : s
        )
      };
    }
    case 'MOVE_TO_SECTION': {
      const { sectionId, targetSectionId } = action.payload;
      const sections = state.sections || [];
      const sourceSection = sections.find(s => s.id === sectionId);
      if (!sourceSection) return state;
      const comp = { id: 'comp_' + sectionId, type: sourceSection.type, label: sourceSection.label, config: { ...sourceSection.config } };
      return {
        ...state,
        sections: sections.filter(s => s.id !== sectionId).map(s =>
          s.id === targetSectionId ? { ...s, config: { ...s.config, components: [...(s.config?.components || []), comp] } } : s
        )
      };
    }
    case 'MOVE_COMPONENT_OUT': {
      const { sourceSectionId, componentId } = action.payload;
      const sections = state.sections || [];
      const sourceSection = sections.find(s => s.id === sourceSectionId);
      if (!sourceSection) return state;
      const comp = (sourceSection.config?.components || []).find(c => c.id === componentId);
      if (!comp) return state;
      const newSection = { id: comp.id.replace('comp_', 'sec_'), type: comp.type, label: comp.label, config: { ...comp.config } };
      const sourceIdx = sections.indexOf(sourceSection);
      const newSections = sections.map(s =>
        s.id === sourceSectionId ? { ...s, config: { ...s.config, components: (s.config?.components || []).filter(c => c.id !== componentId) } } : s
      );
      newSections.splice(sourceIdx + 1, 0, newSection);
      return { ...state, sections: newSections };
    }
    case 'UPDATE_STYLES':
      return { ...state, styles: { ...state.styles, ...action.payload } };
    case 'UPDATE_PAGE_SETTINGS':
      return { ...state, pageSettings: { ...state.pageSettings, ...action.payload } };
    case 'UPDATE_HEADER':
      return { ...state, header: { ...state.header, ...action.payload } };
    case 'UPDATE_FOOTER':
      return { ...state, footer: { ...state.footer, ...action.payload } };
    case 'ADD_COMPONENT': {
      const { sectionId, componentType, label } = action.payload;
      const newComp = {
        id: generateId('comp'),
        type: componentType,
        label,
        config: { ...(DEFAULT_CONFIGS[componentType] || {}) }
      };
      return {
        ...state,
        sections: (state.sections || []).map(s =>
          s.id === sectionId
            ? { ...s, config: { ...s.config, components: [...(s.config.components || []), newComp] } }
            : s
        )
      };
    }
    case 'REMOVE_COMPONENT': {
      const { sectionId: sid2, componentId } = action.payload;
      return {
        ...state,
        sections: (state.sections || []).map(s =>
          s.id === sid2
            ? { ...s, config: { ...s.config, components: (s.config.components || []).filter(c => c.id !== componentId) } }
            : s
        )
      };
    }
    case 'UPDATE_COMPONENT': {
      const { sectionId: sid3, componentId: cid, updates: cUpdates } = action.payload;
      return {
        ...state,
        sections: (state.sections || []).map(s =>
          s.id === sid3
            ? {
                ...s,
                config: {
                  ...s.config,
                  components: (s.config.components || []).map(c =>
                    c.id === cid ? { ...c, config: { ...c.config, ...cUpdates } } : c
                  )
                }
              }
            : s
        )
      };
    }
    case 'REORDER_COMPONENTS': {
      const { sectionId: sid4, components: newComps } = action.payload;
      return {
        ...state,
        sections: (state.sections || []).map(s =>
          s.id === sid4
            ? { ...s, config: { ...s.config, components: newComps } }
            : s
        )
      };
    }
    default:
      return state;
  }
}

const INITIAL_STATE = {
  version: 1,
  pageSettings: { size: 'A4', margins: { top: 50, bottom: 50, left: 50, right: 50 } },
  styles: { headingFont: 'Helvetica-Bold', bodyFont: 'Helvetica', headingSize: 20, bodySize: 11, primaryColor: '#3b82f6', bodyAlignment: 'justify' },
  header: { enabled: true, showLogo: true, showPageNumber: false, text: '' },
  footer: { enabled: true, showPageNumber: true, text: 'CONFIDENTIAL' },
  sections: []
};

/**
 * A header or footer, shown among the sections but not one of them.
 *
 * They repeat on every page, so they carry no position in the flow — hence no
 * drag handle and a fixed spot above the section list. Removing one turns it
 * off and returns it to the palette; nothing it was configured with is lost.
 */
function FurnitureCard({ title, icon, note, onRemove, collapseSignal, children }) {
  const [collapsed, setCollapsed] = useState(false);
  // Collapse All speaks to every card at once. The signal carries the state it
  // wants plus a nonce, so pressing it twice in a row still arrives -- and a
  // card the author has since opened by hand is picked up again next time.
  useEffect(() => {
    if (collapseSignal && collapseSignal.nonce) setCollapsed(collapseSignal.collapsed);
  }, [collapseSignal]);

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 10, marginBottom: 12, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        borderBottom: collapsed ? 'none' : '1px solid var(--border)', background: 'var(--bg-input)',
      }}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{note}</span>
        <button type="button" onClick={() => setCollapsed(c => !c)}
          title={collapsed ? `Expand ${title.toLowerCase()}` : `Collapse ${title.toLowerCase()}`}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
                   color: 'var(--text-secondary)', fontSize: 14, padding: '2px 6px',
                   transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
                   transition: 'transform 0.15s', fontFamily: 'inherit' }}>▼</button>
        <button type="button" onClick={onRemove} title={`Remove ${title.toLowerCase()}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer',
                   color: '#ef4444', fontSize: 17, lineHeight: 1, padding: '0 4px' }}>×</button>
      </div>
      {!collapsed && <div style={{ padding: 14 }}>{children}</div>}
    </div>
  );
}

export default function ReportTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiFetch, isMsp } = useAuth();
  const [template, dispatch] = useReducer(templateReducer, INITIAL_STATE);
  const [meta, setMeta] = useState({ name: '', description: '', isDefault: false, logoData: null });
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'saving' | 'unsaved' | 'error'
  const [activeDrag, setActiveDrag] = useState(null);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);
  const hasLoadedRef = useRef(false);

  // Customer Templates: the sections this organisation has saved.
  const [savedSections, setSavedSections] = useState([]);
  const [savedError, setSavedError] = useState(null);
  // The section waiting to be named, while the dialog is open.
  const [savingSection, setSavingSection] = useState(null);

  // Collapse All. The blocks keep their own open/shut state -- an author
  // opening one by hand must not be undone by a re-render -- so this is a
  // broadcast rather than the source of truth: {collapsed, nonce}, where the
  // nonce is what makes a second press of the same button arrive at all.
  const [collapseSignal, setCollapseSignal] = useState({ collapsed: false, nonce: 0 });
  const allCollapsed = collapseSignal.nonce > 0 && collapseSignal.collapsed;
  const toggleCollapseAll = useCallback(() => {
    setCollapseSignal(s => ({ collapsed: !(s.nonce > 0 && s.collapsed), nonce: s.nonce + 1 }));
  }, []);

  const loadSavedSections = useCallback(async () => {
    try {
      const res = await apiFetch('/saved-sections');
      if (!res.ok) {
        // Said out loud in the palette rather than swallowed: an empty group
        // and a group that failed to load look identical otherwise, and the
        // author would conclude their saved sections were gone.
        setSavedError('Customer Templates could not be loaded.');
        return;
      }
      setSavedSections(await res.json());
      setSavedError(null);
    } catch {
      setSavedError('Customer Templates could not be loaded.');
    }
  }, [apiFetch]);

  useEffect(() => { loadSavedSections(); }, [loadSavedSections]);

  // Load template
  const loadTemplate = useCallback(async () => {
    try {
      const res = await apiFetch(`/report-templates/${id}`);
      if (!res.ok) { navigate('/report-templates'); return; }
      const data = await res.json();
      setMeta({ name: data.name, description: data.description || '', isDefault: data.is_default, logoData: data.logo_data });
      if (data.template_json) {
        dispatch({ type: 'SET_TEMPLATE', payload: data.template_json });
      }
      hasLoadedRef.current = true;
    } catch (err) {
      console.error('Load template error:', err);
    } finally {
      setLoading(false);
    }
  }, [id, apiFetch, navigate]);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  // Auto-save with debounce
  const save = useCallback(async () => {
    if (!hasLoadedRef.current) return;
    setSaveStatus('saving');
    try {
      await apiFetch(`/report-templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: meta.name,
          description: meta.description,
          logoData: meta.logoData,
          isDefault: meta.isDefault,
          templateJson: template
        })
      });
      setSaveStatus('saved');
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('error');
    }
  }, [id, meta, template, apiFetch]);

  // Trigger auto-save on changes
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    setSaveStatus('unsaved');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 2000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [template, meta, save]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event) => {
    const data = event.active.data?.current;
    if (data?.fromPalette) setActiveDrag({ label: data.label, icon: data.type });
    else setActiveDrag(null);
  };

  const handleDragEnd = (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const sections = template.sections || [];

    // Palette drop — active has fromPalette data
    if (active.data?.current?.fromPalette) {
      const { type, label, savedId } = active.data.current;
      const dropIndex = over.id === 'empty-canvas'
        ? undefined
        : (sections.findIndex(s => s.id === over.id) !== -1
            ? sections.findIndex(s => s.id === over.id) + 1 : undefined);

      // A Customer Template carries a saved id instead of a section type.
      if (savedId) {
        const saved = savedSections.find(s => s.id === savedId);
        // Gone from the shelf since the drag began — deleted by someone else,
        // or by this author in another tab. Dropping nothing beats dropping a
        // section with no type, which renders as a blank gap.
        if (saved) dispatch({ type: 'ADD_SAVED_SECTION', payload: { saved, index: dropIndex } });
        return;
      }

      if (type === 'header' || type === 'footer') return;   // not orderable
      dispatch({ type: 'ADD_SECTION', payload: { type, label, index: dropIndex } });
      return;
    }

    // Reorder existing sections
    if (active.id === over.id) return;
    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      dispatch({ type: 'REORDER_SECTIONS', payload: arrayMove(sections, oldIndex, newIndex) });
    }
  };

  const handleAddSection = (type, label) => {
    // Header and footer are document furniture, not flow sections: they live at
    // the top level of the template and repeat on every page. Adding one turns
    // it on; the palette then hides it so a second cannot be added.
    if (type === 'header') return dispatch({ type: 'UPDATE_HEADER', payload: { enabled: true } });
    if (type === 'footer') return dispatch({ type: 'UPDATE_FOOTER', payload: { enabled: true } });
    dispatch({ type: 'ADD_SECTION', payload: { type, label } });
  };

  const handleAddSaved = (saved) => dispatch({ type: 'ADD_SAVED_SECTION', payload: { saved } });

  /** Put a section on the organisation's shelf, under the name they give it. */
  const handleSaveSectionAs = async (name, description) => {
    const section = savingSection;
    if (!section) return;
    const res = await apiFetch('/saved-sections', {
      method: 'POST',
      body: JSON.stringify({
        name, description,
        // The id is left behind: it belongs to this template's copy, and every
        // insert gets a fresh one.
        section: { type: section.type, label: section.label, config: section.config || {} },
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'The section could not be saved.');
    }
    const created = await res.json();
    setSavedSections(list => [...list, created].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
    // A message about an earlier failure, still sitting above a list that has
    // just grown, reads as though this save failed too.
    setSavedError(null);
    setSavingSection(null);
  };

  const handleDeleteSaved = async (saved) => {
    if (!window.confirm(
      `Remove "${saved.name}" from Customer Templates?\n\n` +
      'Templates that already use it keep their own copy — this only takes it off the palette.'
    )) return;
    // try/catch, because this runs as an unawaited async onClick: a rejected
    // fetch -- the backend restarting, the laptop off the network -- would
    // otherwise disappear with nothing shown and the entry still on screen.
    try {
      const res = await apiFetch(`/saved-sections/${saved.id}`, { method: 'DELETE' });
      // 404 means somebody else already removed it, which is the outcome the
      // author asked for. Reporting that as a failure would leave a row in the
      // palette that no longer exists.
      if (res.ok || res.status === 404) {
        setSavedSections(list => list.filter(s => s.id !== saved.id));
        setSavedError(null);
        return;
      }
      const body = await res.json().catch(() => ({}));
      setSavedError(body.error || 'That saved section could not be removed.');
    } catch {
      setSavedError('That saved section could not be removed — check your connection.');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setMeta(m => ({ ...m, logoData: reader.result }));
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading template...</div>;
  }

  const statusColors = { saved: '#22c55e', saving: '#f97316', unsaved: '#6b7280', error: '#ef4444' };
  const statusText = { saved: 'Saved ✓', saving: 'Saving...', unsaved: 'Unsaved', error: 'Save failed' };

  return (
    <div style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', margin: '-24px', marginTop: '-16px' }}>
      {/* Top Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--bg-card)', flexShrink: 0
      }}>
        <Button variant="ghost" onClick={() => navigate('/report-templates')} style={{ padding: '6px 12px', fontSize: 13 }}>
          ← Back
        </Button>
        <Input
          value={meta.name}
          onChange={e => setMeta(m => ({ ...m, name: e.target.value }))}
          placeholder="Template Name"
          style={{ marginBottom: 0, flex: 1, maxWidth: 280 }}
        />
        <Input
          value={meta.description}
          onChange={e => setMeta(m => ({ ...m, description: e.target.value }))}
          placeholder="Template description (optional)"
          style={{ marginBottom: 0, flex: 1, maxWidth: 320, fontSize: 12, color: 'var(--text-secondary)' }}
        />
        <span style={{ fontSize: 12, color: statusColors[saveStatus], fontWeight: 500 }}>
          {statusText[saveStatus]}
        </span>
        {/* One press shuts every band, the header and the footer, so a long
            template can be reordered without scrolling past its contents. */}
        <Button variant="ghost" onClick={toggleCollapseAll}
          title={allCollapsed ? 'Expand every section, header and footer'
                              : 'Collapse every section, header and footer'}
          style={{ padding: '6px 12px', fontSize: 13 }}>
          {allCollapsed ? 'Expand All' : 'Collapse All'}
        </Button>
        <Button onClick={save} style={{ padding: '6px 16px', fontSize: 13 }}>Save Now</Button>
      </div>

      {/* Three-Panel Layout */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Panel: Palette + Settings */}
        <div style={{
          width: 240, flexShrink: 0, borderRight: '1px solid var(--border)',
          padding: 12, overflowY: 'auto', background: 'var(--bg)'
        }}>
          <SectionPalette onAddSection={handleAddSection}
            hidden={[
              ...(template.header?.enabled !== false ? ['header'] : []),
              ...(template.footer?.enabled !== false ? ['footer'] : []),
              // MSP-only, and only as a question of who is OFFERED it. A
              // template that already contains one still renders for whoever
              // can generate that report -- templates are shared objects, and
              // this gate is about the palette, not about output.
              ...(isMsp ? [] : ['tsr-dashboard', 'tsr-findings-walkthrough', 'owasp-findings', 'finding-cards']),
            ]}
            savedSections={savedSections}
            savedError={savedError}
            onAddSaved={handleAddSaved}
            onDeleteSaved={handleDeleteSaved} />
          <PageSettingsPanel
            styles={template.styles || {}}
            pageSettings={template.pageSettings || {}}
            logoData={meta.logoData}
            onUpdateStyles={s => dispatch({ type: 'UPDATE_STYLES', payload: s })}
            onUpdatePageSettings={p => dispatch({ type: 'UPDATE_PAGE_SETTINGS', payload: p })}
            onLogoUpload={handleLogoUpload}
            onLogoRemove={() => setMeta(m => ({ ...m, logoData: null }))}
          />
        </div>

        {/* Center Panel: Section Editor */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: 'var(--bg)' }}>
            {template.header?.enabled !== false && (
              <FurnitureCard title="Header" icon="⬆️" collapseSignal={collapseSignal}
                note="Repeats at the top of every page."
                onRemove={() => dispatch({ type: 'UPDATE_HEADER', payload: { enabled: false } })}>
                <HeaderFooterEditor kind="header" config={template.header || {}}
                  onUpdate={h => dispatch({ type: 'UPDATE_HEADER', payload: h })} wide />
              </FurnitureCard>
            )}
            {template.footer?.enabled !== false && (
              <FurnitureCard title="Footer" icon="⬇️" collapseSignal={collapseSignal}
                note="Repeats at the bottom of every page."
                onRemove={() => dispatch({ type: 'UPDATE_FOOTER', payload: { enabled: false } })}>
                <HeaderFooterEditor kind="footer" config={template.footer || {}}
                  onUpdate={f => dispatch({ type: 'UPDATE_FOOTER', payload: f })} wide />
              </FurnitureCard>
            )}
            <SectionList
              sections={template.sections || []}
              onUpdateSection={(sectionId, updates) => dispatch({ type: 'UPDATE_SECTION', payload: { id: sectionId, updates } })}
              onRemoveSection={sectionId => dispatch({ type: 'REMOVE_SECTION', payload: sectionId })}
              dispatch={dispatch}
              onSaveSection={setSavingSection}
              collapseSignal={collapseSignal}
            />
        </div>

        {/* Right Panel: Preview */}
        <div style={{
          width: 400, flexShrink: 0, borderLeft: '1px solid var(--border)',
          padding: 12, overflowY: 'auto', background: '#f8fafc'
        }}>
          <VariableReference />
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 8, marginTop: 12 }}>
            Preview
          </div>
          <TemplatePreview template={template} />
        </div>
      </div>
      <DragOverlay>
        {activeDrag && (
          <div style={{
            padding: '8px 14px', background: 'var(--primary)', color: '#fff',
            borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap'
          }}>
            + {activeDrag.label}
          </div>
        )}
      </DragOverlay>
      </DndContext>

      {savingSection && (
        <SaveSectionDialog
          section={savingSection}
          onCancel={() => setSavingSection(null)}
          onSave={handleSaveSectionAs}
        />
      )}
    </div>
  );
}

/**
 * Name a section before it goes on the organisation's shelf.
 *
 * The name is required and is the whole point: it is what the section is
 * called in the palette and, from then on, what it is called on the canvas
 * when anyone adds it.
 */
function SaveSectionDialog({ section, onCancel, onSave }) {
  const [name, setName] = useState(section.label || '');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError('Give it a name so people can find it.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave(trimmed, description.trim());
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const parts = (section.config?.components || []).length;

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', padding: 20, width: 420, maxWidth: '100%',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Save to Customer Templates</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
          Everything in this section is kept, text included
          {parts > 0 ? `, all ${parts} part${parts === 1 ? '' : 's'} of it` : ''}. Everyone in your
          organisation will be able to add it to their own templates.
        </div>

        <Input label="Name" value={name} autoFocus
          placeholder="e.g. Standard Scope and Approach"
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
        <Input label="Description (optional)" value={description}
          placeholder="What it is for, or when to use it"
          onChange={e => setDescription(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }} />

        {error && (
          <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4, marginBottom: 4 }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <Button variant="secondary" onClick={onCancel} disabled={busy}
            style={{ padding: '6px 14px', fontSize: 13 }}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            style={{ padding: '6px 14px', fontSize: 13 }}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  );
}
